import { appStore, LocalStorageService, MAX_INT32_VALUE } from '@tom-ui/utils'
import {
    AllocationRuleDto,
    BerthResponse,
    CarrierType,
    ConfigAreaDto,
    ConfigAreaType,
    ContainerHeight,
    ContainerType,
    LayoutObjectDto,
    RailTrackResponse,
    ReeferPlugDirection,
    YardBlockDto,
} from '@yard/app/api'
import { TransportSelectionProps } from '@yard/app/models/host/transport-selection.model'
import { ContainerLength } from '@yard/components/AutoCompletes/ContainerLengthAutoComplete/ContainerLengthAutoComplete'
import { CompanyStore } from '@yard/stores/CompanyStore'
import { LayoutObjectStore } from '@yard/stores/LayoutObjectStore'
import { OccupancyStore, OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { BerthStore } from '@yard/stores/berth.store'
import { JourneyStore } from '@yard/stores/journey.store'
import { RailTrackStore } from '@yard/stores/rail-track.store'
import { OTHER_REST_VALUE, paletteColorFor } from '@yard/utils/color-scheme-palette'
import { getSchemeValue, isOccupancyMatchingFilters } from '@yard/utils/occupancies.utils'
import { action, computed, makeObservable, observable } from 'mobx'

import { ImoClassAutoCompleteProps } from '@tom-ui/planning'
import { ReactNode } from 'react'
import { ContainerViewMode, generateSlotKey } from '../helpers/yard-view.utils'
import {
    FlexibleBlockWithOccupancies,
    OccupancyWithFilter,
    ReeferPlugAreasByBay,
    StackConfigArea,
    YardBlockWithOccupancies,
} from '../models/yard-view.model'
import { CarrierVisitAllocationRulesViewStore } from './allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from './allocation-rules/global-allocation-rules.view-store'
import { CarrierVisitOverviewViewStore } from './carrier-visit-overview.viewstore'

export type BackgroundMode = 'map' | 'image' | 'none'

export type YardViewPreferences = {
  showAllocationRulesPreview: boolean
  backgroundMode: BackgroundMode
  showLegend: boolean
  showNodeHeaders: boolean
  /** @deprecated use backgroundMode instead */
  showBackgroundImage?: boolean
}

const YARD_VIEW_PREFERENCES_KEY = 'tom.yard.viewPreferences'

export type YardFilters = {
  inboundCarrierType?: CarrierType
  inboundCarrierVisitId?: number
  outboundCarrierType?: CarrierType
  outboundCarrierVisitId?: number

  containerOperatorId?: number

  portOfLoading?: string
  portOfDischarge?: string

  containerNumber?: string
  containerHeight?: ContainerHeight[]
  containerLength?: ContainerLength[]
  containerType?: ContainerType[]

  isEmpty?: boolean
  isDangerous?: boolean
  isReefer?: boolean
  isOOG?: boolean
  isDamaged?: boolean
}

export type ColorScheme =
  | 'portOfDischarge'
  | 'outboundCarrierType'
  | 'inboundCarrierType'
  | 'operator'

export const COLOR_SCHEMES: readonly ColorScheme[] = [
  'portOfDischarge',
  'outboundCarrierType',
  'inboundCarrierType',
  'operator',
]

// Number of distinct values shown by name in the colour-by filter list.
// Anything beyond this collapses into the grey "Rest" bucket.
export const COLOR_SCHEME_TOP_N = 8

// Max ticks the planner can have at once: the top-N named values plus the
// Rest sentinel.
export const COLOR_SCHEME_MAX_SELECTIONS = COLOR_SCHEME_TOP_N + 1

export type ColorSchemeValueCount = { value: string; count: number }

// Row shape used by the colour-by filter list and the legend. `isRest` marks
// the synthetic "Rest" entry that aggregates everything outside the top-N.
export type ColorSchemeRow = ColorSchemeValueCount & { isRest?: boolean }

// Resolves a colour-scheme bucket value to its user-facing display name.
// - For the three string-keyed schemes the value is already the display string.
// - For the 'operator' scheme the value is a stringified operatorId; resolve
//   through CompanyStore.displayNameFor (which falls back to "Unknown (id:N)"
//   when the master data is missing or hasn't loaded yet).
export const resolveSchemeDisplayName = (
  scheme: ColorScheme,
  value: string,
  companyStore: CompanyStore | undefined,
): string => {
  if (scheme !== 'operator') return value
  const id = Number.parseInt(value, 10)
  if (!Number.isFinite(id)) return value
  return companyStore ? companyStore.displayNameFor(id) : `Unknown (id:${id})`
}

export type BlockColorSegment = {
  value: string
  // User-facing label. For the three string-keyed schemes this is the same as
  // `value`. For the operator scheme the hook that builds NodeProps replaces
  // this with the resolved company display name via `CompanyStore`.
  label: string
  count: number
  color: string
}

export type BlockColorDistribution = {
  segments: BlockColorSegment[]
  otherCount: number
  totalCount: number
}

// Minimum on-screen pixel width for a colored distribution segment. Enforced
// as a CSS `min-width` on each colored segment by `BlockColorDistributionBar`,
// so a tiny selected match (e.g. 1 in 1000) stays visible rather than
// disappearing into the white "Other" segment. The "Other" segment shrinks
// to compensate; proportionality is no longer strict, but the user always
// sees that their selection is present.
export const MIN_DISTRIBUTION_SEGMENT_PX = 4

const emptySelectedSchemeValues = (): Record<ColorScheme, string[]> => ({
  portOfDischarge: [],
  outboundCarrierType: [],
  inboundCarrierType: [],
  operator: [],
})

export class YardViewStore {
  isViewMode = false
  isBackgroundEditBlocked = true
  isContainerDetailsOpen = false
  isFilterDrawerOpen = false
  showAllocationRulesPreview = false
  backgroundMode: BackgroundMode = 'image'
  showLegend = true
  showNodeHeaders = false
  filters: YardFilters = {}

  colorScheme: ColorScheme | undefined = undefined
  selectedSchemeValues: Record<ColorScheme, string[]> = emptySelectedSchemeValues()

  createRuleFromFilter = false

  focusedBlockId?: string
  focusedLayoutObjectId?: string
  focusedBerthId?: string
  focusedRailTrackId?: string
  selectedBay?: number
  selectedRow?: number

  private readonly preferencesStorage: LocalStorageService<YardViewPreferences>

  constructor(
    private readonly layoutObjectStore: LayoutObjectStore,
    private readonly occupancyStore: OccupancyStore,
    private readonly yardBlockStore: YardBlockStore,
    private readonly berthStore: BerthStore,
    private readonly railTrackStore: RailTrackStore,
    private readonly globalAllocationRulesViewStore: GlobalAllocationRulesViewStore,
    private readonly carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore,
    private readonly carrierVisitOverviewViewStore: CarrierVisitOverviewViewStore,
    private readonly journeyStore: JourneyStore,
    public transportSelection: (props: TransportSelectionProps) => ReactNode,
    public companyAutoComplete: (props: any) => ReactNode,
    public locationCodeAutoComplete: (props: any) => ReactNode,
    public imoClassAutoComplete: (props: ImoClassAutoCompleteProps) => ReactNode,
    public getContainerJourneysByContainerIds?: (ids: number[]) => Promise<void>,
    preferencesStorage: LocalStorageService<YardViewPreferences> = new LocalStorageService(
      YARD_VIEW_PREFERENCES_KEY,
    ),
  ) {
    this.preferencesStorage = preferencesStorage

    const stored = this.preferencesStorage.getData()
    if (stored) {
      this.showAllocationRulesPreview = stored.showAllocationRulesPreview
      this.showLegend = stored.showLegend
      this.showNodeHeaders = stored.showNodeHeaders
      if (stored.backgroundMode) {
        this.backgroundMode = stored.backgroundMode
      } else if (typeof stored.showBackgroundImage === 'boolean') {
        // Migrate legacy boolean preference
        this.backgroundMode = stored.showBackgroundImage ? 'image' : 'none'
      }
    }

    makeObservable(this, {
      focusedBlockId: observable,

      isViewMode: observable,
      isBackgroundEditBlocked: observable,
      isContainerDetailsOpen: observable,
      isFilterDrawerOpen: observable,
      showAllocationRulesPreview: observable,
      backgroundMode: observable,
      showLegend: observable,
      showNodeHeaders: observable,

      selectedBay: observable,
      selectedRow: observable,
      focusedLayoutObjectId: observable,
      focusedBerthId: observable,
      focusedRailTrackId: observable,

      filters: observable,
      colorScheme: observable,
      selectedSchemeValues: observable,
      createRuleFromFilter: observable,

      setFocusedBlockId: action,

      setIsViewMode: action,
      setIsBackgroundEditBlocked: action,
      setContainerDetailsOpen: action,
      setFilterDrawerOpen: action,
      setShowAllocationRulesPreview: action,
      setBackgroundMode: action,
      setShowLegend: action,
      setShowNodeHeaders: action,

      setFocusedLayoutObjectId: action,
      setFocusedBerthId: action,

      setFilters: action,
      clearFilters: action,
      setColorScheme: action,
      toggleSchemeValue: action,
      setSelectedSchemeValues: action,
      clearSelectedValuesForScheme: action,
      setCreateRuleFromFilter: action,

      selectedValuesForActiveScheme: computed,
      effectiveSelectedValuesForActiveScheme: computed,
      presentValuesByScheme: computed,
      topRowsForActiveScheme: computed,
      isRestSelected: computed,
      yardBlocks: computed,
      flexibleBlocks: computed,
      focusedYardBlock: computed,
      occupanciesForSelectedBayAndRow: computed,
      tierConflictsForSelectedBayAndRow: computed,
      blockOccupancies: computed,
      containerDetailsColumns: computed,
      containerViewMode: computed,
      isFocusedBlockStandard: computed,
      layoutObjects: computed,
      berths: computed,
      railTracks: computed,

      filterCount: computed,
    })
  }

  setIsViewMode = (isViewMode: boolean) => {
    if (this.isViewMode !== isViewMode) this.isViewMode = isViewMode
  }

  setIsBackgroundEditBlocked = (isBlocked: boolean) => {
    if (this.isBackgroundEditBlocked !== isBlocked) this.isBackgroundEditBlocked = isBlocked
  }

  setContainerDetailsOpen = async (isOpen: boolean, bay?: number, row?: number) => {
    if (!this.isViewMode) return

    if (this.isContainerDetailsOpen !== isOpen) this.isContainerDetailsOpen = isOpen

    if (isOpen) {
      this.selectedBay = bay
      this.selectedRow = row

      await this.fetchContainerJourneys()
    }
  }

  setFilterDrawerOpen = (isOpen: boolean) => {
    if (!this.isViewMode) return
    this.isFilterDrawerOpen = isOpen
  }

  setShowAllocationRulesPreview = (show: boolean) => {
    this.showAllocationRulesPreview = show
    this.persistPreferences()
  }

  setBackgroundMode = (mode: BackgroundMode) => {
    this.backgroundMode = mode
    this.persistPreferences()
  }

  setShowLegend = (show: boolean) => {
    this.showLegend = show
    this.persistPreferences()
  }

  setShowNodeHeaders = (show: boolean) => {
    this.showNodeHeaders = show
    this.persistPreferences()
  }

  private persistPreferences() {
    this.preferencesStorage.saveData({
      showAllocationRulesPreview: this.showAllocationRulesPreview,
      backgroundMode: this.backgroundMode,
      showLegend: this.showLegend,
      showNodeHeaders: this.showNodeHeaders,
    })
  }

  setFocusedBlockId = (id?: string) => {
    if (this.focusedBlockId !== id) {
      this.resetFocusedIds()

      this.focusedBlockId = id
      this.setContainerDetailsOpen(false)
    }
  }

  setFocusedLayoutObjectId = (id?: string) => {
    if (this.focusedLayoutObjectId !== id) {
      this.resetFocusedIds()

      this.focusedLayoutObjectId = id
    }
  }

  setFocusedBerthId = (id?: string) => {
    if (this.focusedBerthId !== id) {
      this.resetFocusedIds()

      this.focusedBerthId = id
    }
  }

  setFocusedRailTrackId = (id?: string) => {
    if (this.focusedRailTrackId !== id) {
      this.resetFocusedIds()

      this.focusedRailTrackId = id
    }
  }

  resetFocusedIds = () => {
    this.setContainerDetailsOpen(false)
    this.focusedBlockId = undefined
    this.focusedLayoutObjectId = undefined
    this.focusedBerthId = undefined
    this.focusedRailTrackId = undefined
  }

  setFilters = (filters: Partial<YardFilters>) => {
    this.filters = {
      ...this.filters,
      ...filters,
    }
  }

  clearFilters = () => {
    this.filters = {}
  }

  setColorScheme = (scheme: ColorScheme | undefined) => {
    // Re-clicking the active scheme deselects it. Per-scheme value selections
    // are preserved across activate/deactivate within the session.
    this.colorScheme = this.colorScheme === scheme ? undefined : scheme
  }

  toggleSchemeValue = (scheme: ColorScheme, value: string) => {
    const current = this.selectedSchemeValues[scheme]
    const idx = current.indexOf(value)
    if (idx >= 0) {
      this.selectedSchemeValues[scheme] = current.filter(v => v !== value)
      return
    }
    if (current.length >= COLOR_SCHEME_MAX_SELECTIONS) return
    this.selectedSchemeValues[scheme] = [...current, value]
  }

  // Bulk replace; truncated at COLOR_SCHEME_MAX_SELECTIONS so callers can pass
  // a pre-sorted list (top-by-count) without having to slice themselves.
  setSelectedSchemeValues = (scheme: ColorScheme, values: ReadonlyArray<string>) => {
    this.selectedSchemeValues[scheme] = values.slice(0, COLOR_SCHEME_MAX_SELECTIONS)
  }

  clearSelectedValuesForScheme = (scheme: ColorScheme) => {
    this.selectedSchemeValues[scheme] = []
  }

  get selectedValuesForActiveScheme(): string[] {
    return this.colorScheme ? this.selectedSchemeValues[this.colorScheme] : []
  }

  // Selection used for actual rendering (slots, block bars, legend). Filters
  // out a stale Rest sentinel when the yard's distinct values for the active
  // scheme have dropped to or below the top-N — Rest can't appear in the
  // list, so it must not act either. Named carry-overs (values still selected
  // but pushed out of the top-N) stay in the set so they keep highlighting.
  get effectiveSelectedValuesForActiveScheme(): string[] {
    const raw = this.selectedValuesForActiveScheme
    if (!raw.includes(OTHER_REST_VALUE)) return raw
    const restApplicable = this.topRowsForActiveScheme.some(r => r.isRest)
    return restApplicable ? raw : raw.filter(v => v !== OTHER_REST_VALUE)
  }

  // Rows the planner sees in the filter list and the legend:
  //   - up to COLOR_SCHEME_TOP_N named values (top by container count)
  //   - a synthetic Rest row when the yard holds more distinct values than the
  //     top-N can show, aggregating everything outside the top-N
  // The Rest count is intentionally stable — it is the sum of the *non-top-N*
  // values' counts, not "containers not in the planner's selection". This
  // means ticking/unticking real values doesn't make the Rest figure jump.
  get topRowsForActiveScheme(): ColorSchemeRow[] {
    if (!this.colorScheme) return []
    const all = this.presentValuesByScheme[this.colorScheme]
    if (all.length <= COLOR_SCHEME_TOP_N) return all
    const top = all.slice(0, COLOR_SCHEME_TOP_N)
    const restCount = all.slice(COLOR_SCHEME_TOP_N).reduce((sum, r) => sum + r.count, 0)
    return [...top, { value: OTHER_REST_VALUE, count: restCount, isRest: true }]
  }

  // True only when the planner has Rest effectively active — i.e. Rest is
  // both ticked AND applicable (the yard's distinct values exceed the
  // top-N). If the yard shrinks so everything fits, a stale Rest tick goes
  // inert — no grey paint, no legend entry.
  get isRestSelected(): boolean {
    return this.effectiveSelectedValuesForActiveScheme.includes(OTHER_REST_VALUE)
  }

  get presentValuesByScheme(): Record<ColorScheme, ColorSchemeValueCount[]> {
    const counts: Record<ColorScheme, Map<string, number>> = {
      portOfDischarge: new Map(),
      outboundCarrierType: new Map(),
      inboundCarrierType: new Map(),
      operator: new Map(),
    }

    for (const occupancies of Object.values(this.occupancyStore.occupanciesByBlock)) {
      for (const occ of occupancies) {
        for (const scheme of COLOR_SCHEMES) {
          const v = getSchemeValue(occ, scheme)
          if (v == null) continue
          const m = counts[scheme]
          m.set(v, (m.get(v) ?? 0) + 1)
        }
      }
    }

    const result = {} as Record<ColorScheme, ColorSchemeValueCount[]>
    for (const scheme of COLOR_SCHEMES) {
      result[scheme] = Array.from(counts[scheme].entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count)
    }
    return result
  }

  blockDistribution(blockId: string): BlockColorDistribution {
    const empty: BlockColorDistribution = { segments: [], otherCount: 0, totalCount: 0 }

    const scheme = this.colorScheme
    if (!scheme) return empty

    const selected = this.selectedSchemeValues[scheme]
    if (selected.length === 0) return empty

    const occupancies = this.occupancyStore.occupanciesByBlock[blockId] ?? []
    if (occupancies.length === 0) return empty

    const selectedSet = new Set(selected)
    const counts = new Map<string, number>()
    let otherCount = 0

    for (const occ of occupancies) {
      const value = getSchemeValue(occ, scheme)
      if (value != null && selectedSet.has(value)) {
        counts.set(value, (counts.get(value) ?? 0) + 1)
      } else {
        otherCount++
      }
    }

    const totalCount = occupancies.length

    const segments: BlockColorSegment[] = Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        label: value,
        count,
        color: paletteColorFor(scheme, value),
      }))
      .sort((a, b) => b.count - a.count)

    return { segments, otherCount, totalCount }
  }

  setCreateRuleFromFilter(value: boolean) {
    this.createRuleFromFilter = value
  }

  get filterCount(): number {
    return Object.values(this.filters).filter(
      filterValue =>
        filterValue !== undefined &&
        filterValue !== null &&
        !(Array.isArray(filterValue) && filterValue.length === 0),
    ).length
  }

  get containerViewMode(): ContainerViewMode {
    if (this.selectedBay && this.selectedRow) return ContainerViewMode.STACK

    if (this.selectedBay) return ContainerViewMode.BAY

    return ContainerViewMode.ROW
  }

  get occupanciesForSelectedBayAndRow(): Record<number, Record<number, OccupancyWithFilter>> {
    const result: Record<number, Record<number, OccupancyWithFilter>> = {}
    if (!this.focusedBlockId) return result

    const block = this.yardBlocks.find(b => b.id === this.focusedBlockId)
    if (!block || block.type !== 'Standard') return result

    Object.entries(block.occupancies ?? {}).forEach(([key, occupancies]) => {
      const [bay, row] = key.split('-').map(Number)

      if (this.selectedBay && this.selectedBay !== bay) return
      if (this.selectedRow && this.selectedRow !== row) return

      let columnIndex = 0
      if (this.containerViewMode === ContainerViewMode.STACK) {
        columnIndex = 0
      } else if (this.containerViewMode === ContainerViewMode.BAY) {
        columnIndex = row - 1
      } else if (this.containerViewMode === ContainerViewMode.ROW) {
        columnIndex = bay - 1
      }

      for (const occupancy of occupancies) {
        if (!occupancy.position) continue
        const tier = occupancy.position.tier

        if (!result[tier]) result[tier] = {}
        result[tier][columnIndex] = occupancy
      }
    })

    return result
  }

  // Sibling of `occupanciesForSelectedBayAndRow`. When two occupancies report
  // the same (bay, row, tier), `occupanciesForSelectedBayAndRow` keeps the last
  // one (Object assignment); this computed records the container numbers that
  // were hidden by that overwrite so the drawer can flag the conflict to the
  // user. Indicates a data-integrity issue from the yard service — physically
  // a tier holds at most one container.
  get tierConflictsForSelectedBayAndRow(): Record<number, Record<number, string[]>> {
    const result: Record<number, Record<number, string[]>> = {}
    if (!this.focusedBlockId) return result

    const block = this.yardBlocks.find(b => b.id === this.focusedBlockId)
    if (!block || block.type !== 'Standard') return result

    Object.entries(block.occupancies ?? {}).forEach(([key, occupancies]) => {
      const [bay, row] = key.split('-').map(Number)

      if (this.selectedBay && this.selectedBay !== bay) return
      if (this.selectedRow && this.selectedRow !== row) return

      let columnIndex = 0
      if (this.containerViewMode === ContainerViewMode.BAY) {
        columnIndex = row - 1
      } else if (this.containerViewMode === ContainerViewMode.ROW) {
        columnIndex = bay - 1
      }

      const seen: Record<number, string[]> = {}
      for (const occupancy of occupancies) {
        if (!occupancy.position) continue
        const tier = occupancy.position.tier
        if (!seen[tier]) seen[tier] = []
        seen[tier].push(occupancy.containerNumber ?? '(unknown)')
      }

      for (const [tierStr, containers] of Object.entries(seen)) {
        if (containers.length <= 1) continue
        const tier = Number(tierStr)
        if (!result[tier]) result[tier] = {}
        // The last occupancy of the duplicate group is the one currently
        // rendered in the drawer; surface the rest as "hidden".
        result[tier][columnIndex] = containers.slice(0, -1)
      }
    })

    return result
  }

  get blockOccupancies(): OcuppancyWithFilterData[] {
    if (!this.focusedBlockId) return []
    return this.occupancyStore.occupanciesByBlock[this.focusedBlockId] || []
  }

  get containerDetailsColumns(): string[] {
    if (!this.focusedYardBlock) return []

    if (this.containerViewMode === ContainerViewMode.STACK) return []

    if (this.containerViewMode === ContainerViewMode.BAY) return this.focusedYardBlock.rowNames

    return this.focusedYardBlock.bayNames
  }

  get isFocusedBlockStandard(): boolean {
    if (!this.focusedYardBlock) return false
    return this.focusedYardBlock.type === 'Standard'
  }

  get focusedYardBlock(): YardBlockWithOccupancies | undefined {
    return this.focusedBlockId ? this.yardBlocks.find(b => b.id === this.focusedBlockId) : undefined
  }

  get yardBlocks(): YardBlockWithOccupancies[] {
    return this.yardBlockStore.items
      .filter(yardBlock => yardBlock.type === 'Standard')
      .map<YardBlockWithOccupancies>(yardBlock => {
        const occupancies = this.occupancyStore.occupanciesByBlock[yardBlock.id] || []
        const mappedOccupancies: Record<string, OccupancyWithFilter[]> = {}

        occupancies.forEach(occupancy => {
          const key = generateSlotKey(occupancy.position?.bay ?? 1, occupancy.position?.row ?? 1)
          if (!mappedOccupancies[key]) {
            mappedOccupancies[key] = []
          }

          mappedOccupancies[key].push({
            ...occupancy,
            isFiltered: isOccupancyMatchingFilters(occupancy, this.filters),
          })
        })

        let allocationRules: AllocationRuleDto[]
        let selectedAllocationRuleIndex: number | undefined

        const overviewOpen = this.carrierVisitOverviewViewStore.isOverviewOpen
        const cvDrawerOpen = this.carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen
        const cvHovered = this.carrierVisitAllocationRulesViewStore.hoveredRuleIndex
        const globalHovered = this.globalAllocationRulesViewStore.hoveredRuleIndex

        if (overviewOpen && !cvDrawerOpen) {
          allocationRules =
            this.carrierVisitAllocationRulesViewStore.allocationRuleStore.carrierVisitRules
          selectedAllocationRuleIndex = undefined
        } else if (cvDrawerOpen && globalHovered !== undefined) {
          const hoveredGlobalRule =
            this.globalAllocationRulesViewStore.allocationRules[globalHovered]
          const cvRules = this.carrierVisitAllocationRulesViewStore.allocationRules
          allocationRules = hoveredGlobalRule ? [...cvRules, hoveredGlobalRule] : cvRules
          selectedAllocationRuleIndex = hoveredGlobalRule ? cvRules.length : undefined
        } else if (cvHovered !== undefined) {
          allocationRules = this.carrierVisitAllocationRulesViewStore.allocationRules
          selectedAllocationRuleIndex = cvHovered
        } else if (cvDrawerOpen) {
          allocationRules = this.carrierVisitAllocationRulesViewStore.allocationRules
          selectedAllocationRuleIndex = undefined
        } else {
          allocationRules = this.globalAllocationRulesViewStore.allocationRules
          selectedAllocationRuleIndex = globalHovered
        }

        return {
          ...yardBlock,
          allocationRules,
          selectedAllocationRuleIndex,
          hasOccupancies: occupancies.length > 0,
          occupancies: mappedOccupancies,
          stackConfigArea: this.getStackConfigAreaRecord(yardBlock),
          reeferConfigAreaByBay: this.getReeferConfigAreaByBay(yardBlock),
          spaceBetweenBaysByEdge: this.getSpaceBetweenBaysByEdge(yardBlock),
        }
      })
  }

  get flexibleBlocks(): FlexibleBlockWithOccupancies[] {
    return this.yardBlockStore.items
      .filter(yardBlock => yardBlock.type === 'FlexibleBlock')
      .map(yardBlock => {
        const occupancies = this.occupancyStore.occupanciesByBlock[yardBlock.id] || []
        return {
          ...yardBlock,
          occupancies: occupancies,
        }
      })
  }

  get layoutObjects(): LayoutObjectDto[] {
    return this.layoutObjectStore.items
  }

  get berths(): BerthResponse[] {
    return this.berthStore.items
  }

  get railTracks(): RailTrackResponse[] {
    return this.railTrackStore.items
  }

  private async fetchContainerJourneys() {
    if (!this.focusedBlockId) return

    let containerIds: number[] = []
    if (this.isFocusedBlockStandard) {
      containerIds = Object.values(this.occupanciesForSelectedBayAndRow).flatMap(tier =>
        Object.values(tier).map(occupancy => occupancy.containerId),
      )
    } else {
      containerIds = this.blockOccupancies.map(occupancy => occupancy.containerId)
    }

    await appStore.requestWithLoader(
      async () => await this.getContainerJourneysByContainerIds?.(containerIds),
    )
  }

  private getReeferConfigAreaByBay(yardBlock: YardBlockDto): Record<number, ReeferPlugAreasByBay> {
    const result: Record<number, ReeferPlugAreasByBay> = {}
    yardBlock.configAreas?.forEach((area: ConfigAreaDto) => {
      if (area.type === ConfigAreaType.ReeferPlugs) {
        if (!result[area.minBay]) result[area.minBay] = {}
        if (area.reeferPlugDirection === ReeferPlugDirection.Fore) {
          result[area.minBay].fore = area
        } else if (area.reeferPlugDirection === ReeferPlugDirection.Aft) {
          result[area.minBay].aft = area
        }
      }
    })
    return result
  }

  /**
   * Per-edge lookup of SpaceBetweenBays config areas keyed by `minBay`. Used by
   * the bay-axis context menu's mutual-exclusion predicates.
   */
  getSpaceBetweenBaysByEdge(yardBlock: YardBlockDto): Record<number, ConfigAreaDto> {
    const result: Record<number, ConfigAreaDto> = {}
    yardBlock.configAreas?.forEach((area: ConfigAreaDto) => {
      if (area.type === ConfigAreaType.SpaceBetweenBays) {
        result[area.minBay] = area
      }
    })
    return result
  }

  private getStackConfigAreaRecord(yardBlock: YardBlockDto) {
    const { configAreas, maxBays, maxRows } = yardBlock
    const stackConfigArea: Record<string, StackConfigArea> = {}

    configAreas?.forEach((area: ConfigAreaDto) => {
      const maxBay = area.maxBay === MAX_INT32_VALUE ? maxBays : area.maxBay
      const maxRow = area.maxRow === MAX_INT32_VALUE ? maxRows : area.maxRow

      for (let bay = area.minBay; bay <= maxBay; bay++) {
        for (let row = area.minRow; row <= maxRow; row++) {
          const key = generateSlotKey(bay, row)

          if (!stackConfigArea[key])
            stackConfigArea[key] = {
              isBlocked: false,
              bayMaxStackHeight: undefined,
              rowMaxStackHeight: undefined,
              slotMaxStackHeight: undefined,
            }

          if (area.type === ConfigAreaType.IsBlocked) stackConfigArea[key].isBlocked = true

          if (area.type === ConfigAreaType.ReeferPlugs) {
            if (area.reeferPlugDirection === ReeferPlugDirection.Fore)
              stackConfigArea[key].hasForePlugs = true
            else if (area.reeferPlugDirection === ReeferPlugDirection.Aft)
              stackConfigArea[key].hasAftPlugs = true
          }

          if (
            area.type === ConfigAreaType.MaxStackHeight &&
            area.minBay === area.maxBay &&
            area.minRow === area.maxRow
          )
            stackConfigArea[key].slotMaxStackHeight = area.stackHeight!

          if (
            area.type === ConfigAreaType.MaxStackHeight &&
            area.minRow === area.maxRow &&
            area.minBay !== area.maxBay
          )
            stackConfigArea[key].rowMaxStackHeight = area.stackHeight!

          if (
            area.type === ConfigAreaType.MaxStackHeight &&
            area.minBay === area.maxBay &&
            area.minRow !== area.maxRow
          )
            stackConfigArea[key].bayMaxStackHeight = area.stackHeight!
        }
      }
    })

    return stackConfigArea
  }
}
